import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowLeft, Menu, X } from "lucide-react";
import { LAYERS_META } from "./data/layers";

const AtlasLayout = () => {
  const location = useLocation();
  const [layersOpen, setLayersOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHub = location.pathname === "/anatomia/owczarek-niemiecki" || location.pathname === "/anatomia/owczarek-niemiecki/";

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#F5F5F5]">
      <nav className="sticky top-0 z-50 bg-[#0B0B0D]/95 backdrop-blur-md border-b border-[#2A2A2D]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
          <Link to="/anatomia/owczarek-niemiecki" className="flex items-center gap-3 group" data-testid="atlas-logo">
            <div className="w-7 h-7 border border-[#D4AF37]/40 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
              <span className="text-[#D4AF37] text-[10px] font-bold">A</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] block leading-none">ATLAS ANATOMII</span>
              <span className="text-[9px] tracking-[0.15em] text-[#B8B8B8]/40 block">OWCZAREK NIEMIECKI</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/anatomia/owczarek-niemiecki" className={`text-[11px] tracking-[0.15em] transition-colors ${isHub ? "text-[#D4AF37]" : "text-[#B8B8B8] hover:text-[#F5F5F5]"}`}>
              PRZEGLĄD
            </Link>
            <div className="relative" onMouseEnter={() => setLayersOpen(true)} onMouseLeave={() => setLayersOpen(false)}>
              <button className="text-[11px] tracking-[0.15em] text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors flex items-center gap-1" data-testid="atlas-layers-dropdown">
                WARSTWY <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {layersOpen && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 bg-[#141416] border border-[#2A2A2D] min-w-[260px] py-2 shadow-2xl"
                  >
                    {LAYERS_META.map(l => (
                      <Link key={l.id} to={l.ready ? `/anatomia/owczarek-niemiecki/${l.slug}` : "#"}
                        className={`block px-5 py-2.5 text-[11px] tracking-[0.1em] transition-colors ${
                          location.pathname.includes(l.slug)
                            ? "text-[#D4AF37] bg-[#D4AF37]/[0.05]"
                            : l.ready ? "text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D]" : "text-[#B8B8B8]/25 cursor-default"
                        }`}
                      >
                        {l.name} {!l.ready && <span className="text-[8px] ml-2 text-[#D4AF37]/30">WKRÓTCE</span>}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="w-px h-4 bg-[#2A2A2D]" />
            <Link to="/" className="text-[10px] tracking-[0.1em] text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-3 h-3" /> HODOWLA
            </Link>
          </div>

          <button className="md:hidden text-[#B8B8B8]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="atlas-mobile-menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }} className="md:hidden overflow-hidden bg-[#0B0B0D] border-t border-[#2A2A2D]"
            >
              <div className="px-4 py-4 space-y-1">
                <Link to="/anatomia/owczarek-niemiecki" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-[#B8B8B8] hover:text-[#D4AF37]">Przegląd</Link>
                <div className="h-px bg-[#2A2A2D] my-2" />
                {LAYERS_META.map(l => (
                  <Link key={l.id} to={l.ready ? `/anatomia/owczarek-niemiecki/${l.slug}` : "#"} onClick={() => l.ready && setMobileOpen(false)}
                    className={`block py-2.5 text-sm ${l.ready ? "text-[#B8B8B8] hover:text-[#D4AF37]" : "text-[#B8B8B8]/25"}`}
                  >
                    {l.name} {!l.ready && <span className="text-[8px] ml-1 text-[#D4AF37]/30">WKRÓTCE</span>}
                  </Link>
                ))}
                <div className="h-px bg-[#2A2A2D] my-2" />
                <Link to="/" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-[#B8B8B8]/40 hover:text-[#D4AF37]">
                  <ArrowLeft className="w-3.5 h-3.5 inline mr-2" />Wróć do hodowli
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <Outlet />

      <footer className="border-t border-[#2A2A2D] px-4 md:px-8 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-8 mb-8">
            <div>
              <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] block mb-3">ATLAS ANATOMII</span>
              <p className="text-[#B8B8B8]/40 text-xs leading-relaxed">Interaktywny przewodnik po budowie owczarka niemieckiego.</p>
            </div>
            <div>
              <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] block mb-3">WARSTWY</span>
              <div className="space-y-1.5">
                {LAYERS_META.map(l => (
                  <Link key={l.id} to={l.ready ? `/anatomia/owczarek-niemiecki/${l.slug}` : "#"}
                    className={`block text-xs ${l.ready ? "text-[#B8B8B8]/50 hover:text-[#D4AF37]" : "text-[#B8B8B8]/20"} transition-colors`}
                  >{l.name}</Link>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] block mb-3">HODOWLA</span>
              <div className="space-y-1.5">
                <Link to="/" className="block text-xs text-[#B8B8B8]/50 hover:text-[#D4AF37] transition-colors">Wolfe vom Katzenberg</Link>
                <Link to="/psy" className="block text-xs text-[#B8B8B8]/50 hover:text-[#D4AF37] transition-colors">Nasze psy</Link>
                <Link to="/mioty" className="block text-xs text-[#B8B8B8]/50 hover:text-[#D4AF37] transition-colors">Aktualne mioty</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-[#2A2A2D] pt-6 text-center">
            <p className="text-[10px] tracking-[0.1em] text-[#B8B8B8]/30">
              Projekt graficzny i koncepcja &copy; <a href="https://apisdesign.online" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors">apisdesign.online</a> &mdash; Wszelkie prawa zastrzeżone
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AtlasLayout;
